import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService/authService';
import { tokenService } from '../services/tokenService';
import { useLocation, useNavigate } from 'react-router-dom';

interface AuthContextType {
  user: { email: string } | null;
  isLoading: boolean;
  signIn: (email: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<{ email: string } | null>(null);
  const [isLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const allowedRoutes = ['/', '/login/otp', '/verify'];

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    const currentPath = location.pathname;

    const checkTokenValidity = async () => {
      if (token && !allowedRoutes.includes(currentPath)) {
        const validToken = await tokenService.getValidToken();
        if (!validToken) {
          navigate('/');
          return;
        }
      }
    };

    if (token && allowedRoutes.includes(currentPath)) {
      navigate('/dashboard');
    } else if (!token && !allowedRoutes.includes(currentPath)) {
      navigate('/');
    } else {
      checkTokenValidity();
    }
  }, [location.pathname, navigate]);

  const handleSignIn = (email: string) => {
    setUser({ email });
  };

  const handleSignOut = async () => {
    await authService.signOut();
    setUser(null);
    window.location.href = '/';
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, signIn: handleSignIn, signOut: handleSignOut }}>
      {children}
    </AuthContext.Provider>
  );
};
